import React, { useState, useEffect, useCallback } from 'react';

import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import ButtonDropdown from '@amzn/awsui-components-console/button-dropdown';

import { SavedSearchEntry } from 'src/common/savedSearches';

interface SavedSearchesBarProps<TState> {
  /** Reads the current entries. Called on mount and on every change event. */
  readonly getSearches: () => SavedSearchEntry<TState>[];
  /** Deletes one entry by id. */
  readonly removeSearch: (id: string) => void;
  /** Store event signalling a same-tab write. */
  readonly changedEvent: string;
  /** Applies a saved selection. */
  readonly onLoadSearch: (state: TState) => void;
  /** Optional bold prefix, e.g. 'Saved Searches:'. Omitted when absent. */
  readonly label?: string;
  /** Optional secondary hint after the chips, e.g. the maximum-kept note. */
  readonly hint?: string;
  /** Optional `data-testid` on each chip wrapper, for counting in tests. */
  readonly itemTestId?: string;
}

/**
 * Row of saved search chips, each opening a menu to load or remove it.
 *
 * Storage is injected rather than imported so this stays presentational and both
 * pages keep their own store (separate keys, separate change events). Renders
 * nothing when empty, so a page shows no empty band before the first save.
 */
function SavedSearchesBar<TState>({
  getSearches,
  removeSearch,
  changedEvent,
  onLoadSearch,
  label,
  hint,
  itemTestId,
}: SavedSearchesBarProps<TState>) {
  const [searches, setSearches] =
    useState<SavedSearchEntry<TState>[]>(getSearches);

  useEffect(() => {
    const handler = () => setSearches(getSearches());
    // Custom event covers this tab; 'storage' covers other tabs.
    window.addEventListener(changedEvent, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(changedEvent, handler);
      window.removeEventListener('storage', handler);
    };
  }, [changedEvent, getSearches]);

  const handleItemClick = useCallback(
    (searchId: string, itemId: string) => {
      if (itemId === 'remove') {
        removeSearch(searchId);
        return;
      }
      const found = searches.find((search) => search.id === searchId);
      if (found) {
        onLoadSearch(found.state);
      }
    },
    [searches, onLoadSearch, removeSearch],
  );

  if (searches.length === 0) {
    return null;
  }

  return (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      {label && (
        <Box variant="span" fontWeight="bold">
          {label}
        </Box>
      )}
      {searches.map((search) => (
        <span key={search.id} data-testid={itemTestId}>
          <ButtonDropdown
            items={[
              { id: 'load', text: 'Load search' },
              { id: 'remove', text: 'Remove' },
            ]}
            onItemClick={({ detail }) => handleItemClick(search.id, detail.id)}
            variant="normal"
          >
            {search.label}
          </ButtonDropdown>
        </span>
      ))}
      {hint && (
        <Box variant="span" color="text-body-secondary" fontSize="body-s">
          {hint}
        </Box>
      )}
    </SpaceBetween>
  );
}

export default SavedSearchesBar;
